import React, { useState } from "react";
import { SortableList, miniInput, SecondsInput } from "@/admin/SortableList";
import { ImageField } from "@/admin/ImageField";
import { DEFAULT_PROMO } from "@/lib/theme";

const Show = ({ item, up, testId, title }: any) => (
  <label className="flex items-center gap-1.5 text-[11px] whitespace-nowrap" title={title}>
    <input type="checkbox" checked={!item.hidden} onChange={(e) => up({ hidden: !e.target.checked })} data-testid={testId} />Show
  </label>
);

const SOCIAL = /instagram|facebook|tiktok|pinterest|youtube|linkedin|twitter|x\.com|threads|whatsapp|wa\.me|social/i;

export function Links({ links, onChange, testId, articles = false, columns, onMoveTo }: any) {
  return (
    <div className="space-y-1.5">
      <SortableList items={links || []} onChange={onChange} testId={testId} className="space-y-1.5" render={(l, up, i) => (
        <div className="flex flex-wrap items-center gap-2 pr-1">
          <Show item={l} up={up} testId={`${testId}-${i}-visible`} title="Show this link" />
          {(articles || l.on_articles || SOCIAL.test(`${l.label} ${l.url}`)) && (
            <label className="flex items-center gap-1.5 text-[11px] whitespace-nowrap" title="Also show this icon under every Journal article">
              <input type="checkbox" checked={!!l.on_articles} onChange={(e) => up({ on_articles: e.target.checked })} data-testid={`${testId}-${i}-articles`} />Articles
            </label>
          )}
          <div className={`grid grid-cols-2 gap-1.5 flex-1 ${l.hidden ? "opacity-50" : ""}`}>
            <input value={l.label || ""} onChange={(e) => up({ label: e.target.value })} placeholder="Link label" className={miniInput} data-testid={`${testId}-label`} />
            <input value={l.url || ""} onChange={(e) => up({ url: e.target.value })} placeholder="/collections/…" className={miniInput} data-testid={`${testId}-url`} />
          </div>
          {onMoveTo && columns.length > 1 && (
            <select value="" onChange={(e) => e.target.value !== "" && onMoveTo(i, Number(e.target.value))} className={miniInput + " !w-auto text-xs"} title="Move this link to another column" data-testid={`${testId}-${i}-move`}>
              <option value="">Move to…</option>
              {columns.map((c: any, n: number) => c.self ? null : <option key={n} value={n}>{c.title || `Column ${n + 1}`}</option>)}
            </select>
          )}
        </div>
      )} />
      <button onClick={() => onChange([...(links || []), { label: "", url: "" }])} className="text-xs underline" data-testid={`${testId}-add`}>+ Add link</button>
    </div>
  );
}

export function Columns({ columns, onChange, testId }: any) {
  const cols = columns || [];
  const moveLink = (from: number, li: number, to: number) => {
    const link = cols[from].links[li];
    onChange(cols.map((c: any, n: number) => n === from ? { ...c, links: c.links.filter((_: any, k: number) => k !== li) } : n === to ? { ...c, links: [...(c.links || []), link] } : c));
  };
  return (
    <div className="space-y-2">
      <SortableList items={cols} onChange={onChange} testId={testId} render={(c, up, i) => (
        <div className="space-y-2 pr-2">
          <div className="flex items-center gap-2">
            <Show item={c} up={up} testId={`${testId}-${i}-visible`} title="Show this whole column" />
            <input value={c.title || ""} onChange={(e) => up({ title: e.target.value })} placeholder="Column title" className={miniInput + ` font-medium flex-1 ${c.hidden ? "opacity-50" : ""}`} data-testid={`${testId}-title`} />
          </div>
          <div className={c.hidden ? "opacity-50" : ""}><Links links={c.links} onChange={(v: any) => up({ links: v })} testId={`${testId}-${i}-links`} articles={/social/i.test(c.title || "")}
            columns={cols.map((x: any, n: number) => ({ title: x.title, self: n === i }))} onMoveTo={(li: number, to: number) => moveLink(i, li, to)} /></div>
        </div>
      )} />
      <button onClick={() => onChange([...(columns || []), { title: "New column", links: [] }])} className="text-xs underline" data-testid={`${testId}-add`}>+ Add column</button>
    </div>
  );
}

function MenuItem({ m, up, i }: any) {
  const [open, setOpen] = useState(false);
  const pv = m.promo || DEFAULT_PROMO;
  return (
    <div className="pr-2">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-[11px] whitespace-nowrap" title="Show this item in the main menu">
          <input type="checkbox" checked={!m.hidden} onChange={(e) => up({ hidden: !e.target.checked })} data-testid={`menu-${i}-visible`} />Show
        </label>
        <div className={`grid grid-cols-2 gap-1.5 flex-1 ${m.hidden ? "opacity-50" : ""}`}>
          <input value={m.label || ""} onChange={(e) => up({ label: e.target.value })} placeholder="Menu label" className={miniInput + " font-medium"} data-testid={`menu-${i}-label`} />
          <input value={m.url || ""} onChange={(e) => up({ url: e.target.value })} placeholder="Link" className={miniInput} data-testid={`menu-${i}-url`} />
        </div>
        <button onClick={() => setOpen(!open)} className="text-[11px] underline whitespace-nowrap" data-testid={`menu-${i}-expand`}>{open ? "Close" : "Mega-menu"} ({m.columns?.length || 0})</button>
      </div>
      {open && <div className="mt-3 pl-2 border-l-2 border-neutral-100 space-y-3">
        <Columns columns={m.columns} onChange={(v: any) => up({ columns: v })} testId={`menu-${i}-cols`} />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Feature tile (right side)</p>
          <div className="grid grid-cols-3 gap-1.5">
            <input value={pv.eyebrow ?? ""} onChange={(e) => up({ promo: { ...pv, eyebrow: e.target.value } })} placeholder="Small label e.g. Bespoke" className={miniInput} data-testid={`menu-${i}-promo-eyebrow`} />
            <input value={pv.title ?? ""} onChange={(e) => up({ promo: { ...pv, title: e.target.value } })} placeholder="Title (blank = no tile)" className={miniInput} data-testid={`menu-${i}-promo-title`} />
            <input value={pv.url ?? ""} onChange={(e) => up({ promo: { ...pv, url: e.target.value } })} placeholder="Link" className={miniInput} data-testid={`menu-${i}-promo-url`} />
          </div>
          <div className="mt-1.5"><ImageField value={pv.image} onChange={(u) => up({ promo: { ...pv, image: u } })} compact testId={`menu-${i}-promo-image`} /></div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5">Schedule (optional) — show only between these dates</p>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <input type="date" value={m.show_from || ""} onChange={(e) => up({ show_from: e.target.value })} className={miniInput + " w-auto"} data-testid={`menu-${i}-show-from`} />
            <span>to</span>
            <input type="date" value={m.show_until || ""} onChange={(e) => up({ show_until: e.target.value })} className={miniInput + " w-auto"} data-testid={`menu-${i}-show-until`} />
            {(m.show_from || m.show_until) && <button type="button" onClick={() => up({ show_from: "", show_until: "" })} className="underline text-neutral-500">clear</button>}
          </div>
        </div>
      </div>}
    </div>
  );
}

export function MenuCloseField({ s, set }: { s: any; set: (k: string, v: any) => void }) {
  return (
    <div className="text-xs text-neutral-600 mb-5 border border-neutral-200 p-3 space-y-3" data-testid="menu-close-setting">
      <label className="flex flex-wrap items-center gap-2">
        Mac / PC: fold menus back up
        <SecondsInput value={s.menu_close_seconds} def={3} step={0.5} onChange={(v) => set("menu_close_seconds", v)} testId="menu-close-seconds-input" />
        seconds after the mouse leaves (menu stays open while hovering). Also used for the phone menu.
      </label>
      <label className="flex flex-wrap items-center gap-2">
        iPad / touch screens: fold menus back up
        <SecondsInput value={s.menu_close_seconds_touch} def={5} step={0.5} onChange={(v) => set("menu_close_seconds_touch", v)} testId="menu-close-seconds-touch-input" />
        seconds after a menu item is tapped.
      </label>
    </div>
  );
}

export function HeaderMenuEditor({ menu, onChange }: { menu: any[]; onChange: (v: any[]) => void }) {
  return (
    <div data-testid="header-menu-editor">
      <SortableList items={menu || []} onChange={onChange} testId="menu" render={(m, up, i) => <MenuItem m={m} up={up} i={i} />} />
      <button onClick={() => onChange([...(menu || []), { label: "New item", url: "/", columns: [] }])} className="mt-3 text-xs underline" data-testid="menu-add-item">+ Add menu item</button>
    </div>
  );
}
